import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { Button, Form, Table } from 'react-bootstrap';
import { db, auth } from '../../firebase/firebase-config';
import { useTenant } from '../../context/TenantContext';
import { useUser } from '../../context/UserContext';
import { useMembership } from '../../context/MembershipContext';
import { withTenantScope } from '../../services/contentWriteFields';
import { fetchTenantMemberUsers, isChatAdmin } from '../../services/chatService';
import {
  DEFAULT_CHAT_LIST_GROUPS,
  describeChatRoomAccess,
  findDuplicateChatListGroupLabel,
  isDefaultChatListGroupId,
  resolveChatListGroups,
  resolveChatRoomCreatorLabel,
  resolveRoomListGroupId,
  slugifyGroupId,
  validateChatListGroupLabels,
} from '../../services/chatListGroups';
import { saveChatListGroups } from '../../services/tenantsService';
import ModulePageHeader from '../common/ModulePageHeader';
import AllowedUsersPicker from './AllowedUsersPicker';
import './Chat.css';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';

function ChatRoomManagement() {
  const navigate = useNavigate();
  const { tenantId, config, reloadConfig } = useTenant();
  const { user } = useUser();
  const { memberships } = useMembership();
  const confirm = useConfirm();
  const toast = useToast();
  const isAdmin = isChatAdmin({ memberships, tenantId, legacyUser: user });

  const [chatRooms, setChatRooms] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [roomName, setRoomName] = useState('');
  const [roomDescription, setRoomDescription] = useState('');
  const [newRoomAllowedUsers, setNewRoomAllowedUsers] = useState([]);
  const [newRoomListGroupId, setNewRoomListGroupId] = useState('rooms');
  const [editingRoom, setEditingRoom] = useState(null);

  const [groupDrafts, setGroupDrafts] = useState([]);
  const [newGroupLabel, setNewGroupLabel] = useState('');

  const listGroups = useMemo(
    () => resolveChatListGroups(config),
    [config],
  );

  useEffect(() => {
    setGroupDrafts(listGroups);
  }, [listGroups]);

  useEffect(() => {
    if (!tenantId || !isAdmin) return;
    fetchChatRooms();
    fetchTenantUsers();
  }, [tenantId, isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/chat" replace />;
  }

  const fetchChatRooms = async () => {
    if (!tenantId) return;
    const snapshot = await getDocs(
      query(collection(db, 'chatRooms'), where('tenantId', '==', tenantId)),
    );
    const roomsData = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));
    roomsData.sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 999;
      const orderB = typeof b.order === 'number' ? b.order : 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.roomName || '').localeCompare(b.roomName || '');
    });
    setChatRooms(roomsData);
  };

  const fetchTenantUsers = async () => {
    if (!tenantId) return;
    setUsersLoading(true);
    try {
      const users = await fetchTenantMemberUsers(tenantId);
      setTenantUsers(users);
    } catch (error) {
      console.error('Error fetching tenant members:', error);
      setTenantUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!roomName.trim()) {
      toast.error('Ime klepetalnice je obvezno.');
      return;
    }
    if (!tenantId) {
      toast.error('Manjkajoči tenant kontekst.');
      return;
    }

    const createdName = roomName.trim();
    setSaving(true);
    try {
      await addDoc(collection(db, 'chatRooms'), withTenantScope(tenantId, {
        roomName: createdName,
        description: roomDescription,
        allowedUsers: newRoomAllowedUsers,
        createdBy: auth.currentUser?.uid || null,
        isChatRoom: true,
        listGroupId: newRoomListGroupId || 'rooms',
      }));

      setRoomName('');
      setRoomDescription('');
      setNewRoomAllowedUsers([]);
      setNewRoomListGroupId('rooms');
      toast.success(`Klepetalnica «${createdName}» je bila uspešno dodana.`);
      await fetchChatRooms();
    } catch (error) {
      console.error('Error creating chat room:', error);
      toast.error('Klepetalnice ni bilo mogoče dodati. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    const room = chatRooms.find((item) => item.id === roomId);
    if (room?.system) {
      toast.error('Sistemskih klepetalnic ni mogoče izbrisati.');
      return;
    }

    const confirmed = await confirm({
      title: 'Izbriši klepetalnico',
      message: 'Ali ste prepričani, da želite izbrisati to klepetalnico?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'chatRooms', roomId));
      toast.deleted(`Klepetalnica «${room.roomName}» je bila izbrisana.`);
      fetchChatRooms();
    } catch (error) {
      console.error('Error deleting chat room:', error);
      toast.error('Klepetalnice ni bilo mogoče izbrisati.');
    }
  };

  const handleEditRoom = (room) => {
    if (room.system) {
      toast.error('Sistemskih klepetalnic ni mogoče urejati.');
      return;
    }
    setEditingRoom({
      ...room,
      allowedUsers: room.allowedUsers || [],
      listGroupId: room.listGroupId || 'rooms',
    });
  };

  const handleSaveRoom = async () => {
    if (!editingRoom) return;
    if (editingRoom.system) {
      toast.error('Sistemskih klepetalnic ni mogoče urejati.');
      return;
    }

    setSaving(true);
    try {
      const roomDocRef = doc(db, 'chatRooms', editingRoom.id);
      await updateDoc(roomDocRef, {
        roomName: editingRoom.roomName,
        description: editingRoom.description,
        allowedUsers: editingRoom.allowedUsers || [],
        listGroupId: editingRoom.listGroupId || 'rooms',
      });

      toast.success(`Klepetalnica «${editingRoom.roomName}» je bila posodobljena.`);
      setEditingRoom(null);
      await fetchChatRooms();
    } catch (error) {
      console.error('Error updating chat room:', error);
      toast.error('Sprememb ni bilo mogoče shraniti. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleRoomGroupChange = async (roomId, listGroupId) => {
    const room = chatRooms.find((item) => item.id === roomId);
    if (room?.system) {
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'chatRooms', roomId), { listGroupId });
      setChatRooms((prev) =>
        prev.map((room) =>
          (room.id === roomId ? { ...room, listGroupId } : room)),
      );
      toast.success('Skupina klepetalnice je bila posodobljena.');
    } catch (error) {
      console.error('Error updating room group:', error);
      toast.error('Skupine ni bilo mogoče shraniti. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingRoom(null);
  };

  const handleSaveGroups = async () => {
    if (!tenantId) return;

    const labelError = validateChatListGroupLabels(groupDrafts);
    if (labelError) {
      toast.error(labelError);
      return;
    }

    const normalized = groupDrafts
      .map((group, index) => ({
        id: group.id,
        label: String(group.label || '').trim() || group.id,
        order: typeof group.order === 'number' ? group.order : index,
      }))
      .filter((group) => group.id && !isDefaultChatListGroupId(group.id));

    setSaving(true);
    try {
      await saveChatListGroups(tenantId, normalized);
      await reloadConfig();
      toast.success('Skupine klepetalnic so bile shranjene.');
    } catch (error) {
      console.error('Error saving chat list groups:', error);
      toast.error('Skupin ni bilo mogoče shraniti. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGroup = () => {
    const label = newGroupLabel.trim();
    if (!label) {
      toast.error('Vnesite naziv skupine.');
      return;
    }

    const duplicate = findDuplicateChatListGroupLabel(groupDrafts, label);
    if (duplicate) {
      toast.error(
        `Skupina «${label}» že obstaja (kot «${duplicate.label}»). Nazivi skupin morajo biti enolični.`,
      );
      return;
    }

    const baseId = slugifyGroupId(label) || 'skupina';
    let nextId = baseId;
    let suffix = 2;
    const existingIds = new Set(groupDrafts.map((group) => group.id));
    while (existingIds.has(nextId)) {
      nextId = `${baseId}-${suffix}`;
      suffix += 1;
    }

    const maxOrder = groupDrafts.reduce(
      (max, group) => Math.max(max, typeof group.order === 'number' ? group.order : 0),
      0,
    );

    setGroupDrafts((prev) => [
      ...prev,
      { id: nextId, label, order: maxOrder + 1 },
    ]);
    setNewGroupLabel('');
  };

  const handleRemoveGroup = (groupId) => {
    if (DEFAULT_CHAT_LIST_GROUPS.some((group) => group.id === groupId)) {
      toast.error('Privzetih skupin ni mogoče izbrisati.');
      return;
    }

    const roomsInGroup = chatRooms.filter((room) => room.listGroupId === groupId);
    if (roomsInGroup.length > 0) {
      toast.error(
        `Skupina vsebuje ${roomsInGroup.length} klepetalnic. Najprej jih premaknite v drugo skupino.`,
      );
      return;
    }

    setGroupDrafts((prev) => prev.filter((group) => group.id !== groupId));
  };

  const resolveGroupLabel = (groupId) =>
    listGroups.find((group) => group.id === groupId)?.label || groupId;

  const renderGroupSelect = (value, onChange, id, { disabled = false } = {}) => (
    <Form.Select
      id={id}
      size="sm"
      value={value || 'rooms'}
      onChange={(event) => onChange(event.target.value)}
      disabled={saving || disabled}
    >
      {listGroups.map((group) => (
        <option key={group.id} value={group.id}>
          {group.label}
        </option>
      ))}
    </Form.Select>
  );

  return (
    <div className="platform-module-container chat-container">
        <ModulePageHeader
          title="Upravljanje klepetalnic"
          subtitle="Urejanje skupin, klepetalnic in dostopov."
          actions={(
            <Button variant="primary" onClick={() => navigate('/chat')}>
              Nazaj na klepetalnico
            </Button>
          )}
        />

        <div className="chat-management-sections">
          <div className="chat-management-panel">
            <h5 className="chat-management-panel__title">Skupine na seznamu</h5>
            <p className="chat-management-panel__hint">
              Klepetalnice so v klepetu razporejene po skupinah. Privzetih skupin
              (Splošno, Konference, Klepetalnice, Organizacijske enote) ni mogoče izbrisati.
              Nazivi skupin morajo biti enolični.
            </p>
            <Table striped bordered hover responsive className="mb-3">
              <thead>
                <tr>
                  <th>Naziv skupine</th>
                  <th>Vrstni red</th>
                  <th>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {groupDrafts.map((group) => (
                  <tr key={group.id}>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={group.label}
                        onChange={(event) => {
                          const nextLabel = event.target.value;
                          setGroupDrafts((prev) =>
                            prev.map((item) =>
                              (item.id === group.id
                                ? { ...item, label: nextLabel }
                                : item)),
                          );
                        }}
                      />
                    </td>
                    <td style={{ width: '7rem' }}>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={group.order ?? 0}
                        onChange={(event) => {
                          const nextOrder = Number(event.target.value);
                          setGroupDrafts((prev) =>
                            prev.map((item) =>
                              (item.id === group.id
                                ? { ...item, order: nextOrder }
                                : item)),
                          );
                        }}
                      />
                    </td>
                    <td style={{ width: '8rem' }}>
                      {!DEFAULT_CHAT_LIST_GROUPS.some((item) => item.id === group.id) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveGroup(group.id)}
                        >
                          Izbriši
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
              <Form.Group className="flex-grow-1">
                <Form.Label className="mb-1">Nova skupina</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="npr. Vodstvo"
                  value={newGroupLabel}
                  onChange={(event) => setNewGroupLabel(event.target.value)}
                />
              </Form.Group>
              <Button variant="outline-primary" onClick={handleAddGroup} disabled={saving}>
                Dodaj skupino
              </Button>
              <Button variant="primary" onClick={handleSaveGroups} disabled={saving}>
                Shrani skupine
              </Button>
            </div>
          </div>

          <div className="chat-management-panel">
            <h5 className="chat-management-panel__title">Dodaj novo klepetalnico</h5>
            <Form.Group controlId="formRoomName">
              <Form.Label>Ime klepetalnice</Form.Label>
              <Form.Control
                type="text"
                placeholder="Vnesite ime..."
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="formRoomDescription" className="mt-3">
              <Form.Label>Opis klepetalnice</Form.Label>
              <Form.Control
                type="text"
                placeholder="Vnesite opis..."
                value={roomDescription}
                onChange={(e) => setRoomDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="formRoomGroup" className="mt-3">
              <Form.Label>Skupina na seznamu</Form.Label>
              {renderGroupSelect(newRoomListGroupId, setNewRoomListGroupId, 'formRoomGroup')}
            </Form.Group>

            <div className="mt-3">
              <AllowedUsersPicker
                users={tenantUsers}
                selectedUserIds={newRoomAllowedUsers}
                onChange={setNewRoomAllowedUsers}
                idPrefix="new-room"
                loading={usersLoading}
              />
            </div>

            <Button
              variant="primary"
              className="mt-3"
              onClick={handleAddRoom}
              disabled={saving}
            >
              {saving ? 'Shranjujem…' : 'Dodaj klepetalnico'}
            </Button>
          </div>

          <div className="chat-management-panel">
            <h5 className="chat-management-panel__title">Seznam klepetalnic</h5>
            <Table striped bordered hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Naziv</th>
                <th>Opis</th>
                <th>Skupina</th>
                <th>Ustvaril</th>
                <th>Dovoljeni uporabniki</th>
                <th>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {chatRooms.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Ni klepetalnic.
                  </td>
                </tr>
              ) : (
              chatRooms.map((room) => {
                const isEditing = editingRoom && editingRoom.id === room.id;
                const { summary: allowedSummary, title: allowedTitle } = describeChatRoomAccess(
                  room,
                  tenantUsers,
                );
                const creatorLabel = resolveChatRoomCreatorLabel(
                  room,
                  tenantUsers,
                  auth.currentUser?.uid,
                );

                return (
                  <tr key={room.id}>
                    {isEditing ? (
                      <>
                        <td>
                          <Form.Control
                            type="text"
                            value={editingRoom.roomName}
                            onChange={(e) =>
                              setEditingRoom((prev) => ({ ...prev, roomName: e.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={editingRoom.description || ''}
                            onChange={(e) =>
                              setEditingRoom((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                          />
                        </td>
                        <td style={{ minWidth: '10rem' }}>
                          {renderGroupSelect(
                            editingRoom.listGroupId,
                            (nextGroupId) =>
                              setEditingRoom((prev) => ({
                                ...prev,
                                listGroupId: nextGroupId,
                              })),
                            `edit-group-${room.id}`,
                          )}
                        </td>
                        <td>{creatorLabel}</td>
                        <td style={{ minWidth: '240px' }}>
                          <AllowedUsersPicker
                            users={tenantUsers}
                            selectedUserIds={editingRoom.allowedUsers || []}
                            onChange={(next) =>
                              setEditingRoom((prev) => ({ ...prev, allowedUsers: next }))
                            }
                            idPrefix={`edit-room-${room.id}`}
                            loading={usersLoading}
                            compact
                          />
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleSaveRoom}
                              disabled={saving}
                            >
                              Shrani
                            </Button>
                            <Button variant="secondary" size="sm" onClick={handleCancelEdit}>
                              Prekliči
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {room.roomName}
                          {room.system && (
                            <span className="badge bg-secondary ms-2">Sistemska</span>
                          )}
                        </td>
                        <td>{room.description}</td>
                        <td style={{ minWidth: '10rem' }}>
                          {room.system ? (
                            <span className="text-muted">
                              {resolveGroupLabel(resolveRoomListGroupId(room))}
                            </span>
                          ) : (
                            renderGroupSelect(
                              resolveRoomListGroupId(room),
                              (nextGroupId) => handleRoomGroupChange(room.id, nextGroupId),
                              `room-group-${room.id}`,
                            )
                          )}
                        </td>
                        <td>{creatorLabel}</td>
                        <td title={allowedTitle}>{allowedSummary}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {!room.system && (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleEditRoom(room)}
                                >
                                  Uredi
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteRoom(room.id)}
                                >
                                  Izbriši
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              }))}
            </tbody>
          </Table>
          </div>
        </div>
    </div>
  );
}

export default ChatRoomManagement;
